/**
 * EAN-13 and EAN-5 symbol encoding — the barcode on the back of a book.
 *
 * A module is the narrowest bar or space; a symbol is a string of modules, "1" for
 * dark and "0" for light. The tables are the GS1 General Specifications' (also printed
 * in the EAN articles of most references):
 *
 * - EAN-13: start guard 101, six left-hand digits, centre guard 01010, six right-hand
 *   digits, end guard 101 — 95 modules. Left-hand digits use set A (L, odd parity) or
 *   set B (G, even parity), and WHICH digits use which set encodes the first digit,
 *   which has no bars of its own. Right-hand digits all use set C (R).
 * - EAN-5 add-on: guard 1011, then five digits in L or G separated by the delineator
 *   01 — 47 modules. Here the L/G pattern comes from a checksum (3 × the odd positions
 *   + 9 × the even positions, mod 10) that is never printed.
 */

/** Set A — odd parity. */
export const EAN_L = [
  "0001101",
  "0011001",
  "0010011",
  "0111101",
  "0100011",
  "0110001",
  "0101111",
  "0111011",
  "0110111",
  "0001011",
] as const;

/** Set B — even parity; the R pattern read backwards. */
export const EAN_G = [
  "0100111",
  "0110011",
  "0011011",
  "0100001",
  "0011101",
  "0111001",
  "0000101",
  "0010001",
  "0001001",
  "0010111",
] as const;

/** Set C — the bitwise complement of set A. */
export const EAN_R = [
  "1110010",
  "1100110",
  "1101100",
  "1000010",
  "1011100",
  "1001110",
  "1010000",
  "1000100",
  "1001000",
  "1110100",
] as const;

/** Which set each of the six left-hand digits uses, indexed by the first digit. */
export const EAN13_PARITY = [
  "LLLLLL",
  "LLGLGG",
  "LLGGLG",
  "LLGGGL",
  "LGLLGG",
  "LGGLLG",
  "LGGGLL",
  "LGLGLG",
  "LGLGGL",
  "LGGLGL",
] as const;

/** Which set each add-on digit uses, indexed by the add-on checksum. */
export const EAN5_PARITY = [
  "GGLLL",
  "GLGLL",
  "GLLGL",
  "GLLLG",
  "LGGLL",
  "LLGGL",
  "LLLGG",
  "LGLGL",
  "LGLLG",
  "LLGLG",
] as const;

const START_GUARD = "101";
const CENTRE_GUARD = "01010";
const END_GUARD = "101";
const ADDON_GUARD = "1011";
const ADDON_DELINEATOR = "01";

const RE_THIRTEEN_DIGITS = /^\d{13}$/;
const RE_FIVE_DIGITS = /^\d{5}$/;

function encodeDigit(digit: string, set: string): string {
  const value = Number(digit);
  if (set === "G") {
    return EAN_G[value];
  }
  if (set === "R") {
    return EAN_R[value];
  }
  return EAN_L[value];
}

/** The 95 modules of an EAN-13 symbol. Expects 13 digits; does not check the check digit. */
export function ean13Modules(digits: string): string {
  if (!RE_THIRTEEN_DIGITS.test(digits)) {
    throw new Error("EAN-13 needs exactly 13 digits");
  }
  const parity = EAN13_PARITY[Number(digits[0])];
  let left = "";
  for (let index = 0; index < 6; index++) {
    left += encodeDigit(digits[index + 1], parity[index]);
  }
  let right = "";
  for (let index = 7; index < 13; index++) {
    right += encodeDigit(digits[index], "R");
  }
  return `${START_GUARD}${left}${CENTRE_GUARD}${right}${END_GUARD}`;
}

/** 3 × (1st + 3rd + 5th) + 9 × (2nd + 4th), mod 10. */
export function addon5Checksum(digits: string): number {
  const d = [...digits].map(Number);
  return (3 * (d[0] + d[2] + d[4]) + 9 * (d[1] + d[3])) % 10;
}

/** The 47 modules of an EAN-5 add-on, starting with its guard. */
export function addon5Modules(digits: string): string {
  if (!RE_FIVE_DIGITS.test(digits)) {
    throw new Error("EAN-5 needs exactly 5 digits");
  }
  const parity = EAN5_PARITY[addon5Checksum(digits)];
  const characters = [...digits].map((digit, index) =>
    encodeDigit(digit, parity[index])
  );
  return `${ADDON_GUARD}${characters.join(ADDON_DELINEATOR)}`;
}

/** Runs of dark modules as [start, width] pairs, for drawing one rect per bar. */
export function modulesToBars(modules: string): [number, number][] {
  const bars: [number, number][] = [];
  let start = -1;
  for (let index = 0; index <= modules.length; index++) {
    const dark = modules[index] === "1";
    if (dark && start < 0) {
      start = index;
    } else if (!dark && start >= 0) {
      bars.push([start, index - start]);
      start = -1;
    }
  }
  return bars;
}

/**
 * The US-dollar price add-on: 5 followed by the price in cents, four digits.
 *
 * 12.99 → "51299". Prices from $100 up are written 59999, the code the book trade
 * reserves for "$100 or more". Returns null for a negative or non-finite price.
 */
export function usdPriceAddon(dollars: number): string | null {
  if (!Number.isFinite(dollars) || dollars < 0) {
    return null;
  }
  const cents = Math.round(dollars * 100);
  if (cents >= 10_000) {
    return "59999";
  }
  return `5${String(cents).padStart(4, "0")}`;
}
